/*
 * True Relevance themes for Backstage.
 *
 * Design decisions worth stating, because they follow from the brand's rules rather than taste:
 *
 *  - Brand hues are never solid. They tint (20–50% alpha) chips, status marks and selected rows.
 *    Every solid surface is a neutral. This is both the design system's core rule and an
 *    accessibility requirement — measured on white the four hues run 1.44:1 to 2.96:1.
 *  - Ink carries interaction. Primary buttons are ink on white (13.4:1), not a brand hue.
 *  - No gradients. Backstage's default page headers are gradient washes; here every PageTheme is
 *    flat off-white with ink text, built by hand rather than via genPageTheme (which always emits
 *    a linear-gradient).
 *  - Nothing is square-cornered. Cards take the 14px card radius, buttons and chips take pills.
 */

import {
  createUnifiedTheme,
  createBaseThemeOptions,
  palettes,
  type PageTheme,
} from '@backstage/theme';
import {
  alpha,
  blueDeep,
  blueDeeper,
  brand,
  fontFamily,
  motion,
  neutral,
  radius,
  shadow,
  status,
  weight,
} from './trTokens';

/**
 * A flat page header. `genPageTheme` is deliberately not used: it composes
 * `linear-gradient(...)` into backgroundImage, and the brand forbids gradient washes — depth is
 * supposed to come from overlapping transparent shapes, never a wash.
 */
function flatPage(background: string, fontColor: string): PageTheme {
  return {
    colors: [background],
    shape: 'none',
    backgroundImage: 'none',
    fontColor,
  };
}

const lightPages: Record<string, PageTheme> = {
  home: flatPage(neutral.offWhite, neutral.ink),
  documentation: flatPage(neutral.offWhite, neutral.ink),
  tool: flatPage(neutral.offWhite, neutral.ink),
  service: flatPage(neutral.offWhite, neutral.ink),
  website: flatPage(neutral.offWhite, neutral.ink),
  library: flatPage(neutral.offWhite, neutral.ink),
  other: flatPage(neutral.offWhite, neutral.ink),
  app: flatPage(neutral.offWhite, neutral.ink),
  apis: flatPage(neutral.offWhite, neutral.ink),
  card: flatPage(neutral.offWhite, neutral.ink),
};

const darkPages: Record<string, PageTheme> = Object.fromEntries(
  Object.keys(lightPages).map(k => [k, flatPage('#242424', neutral.white)]),
);

/** Type scale. Gabarito Bold for headings, left-aligned; the brand never centres running copy. */
const typography = {
  fontFamily,
  htmlFontSize: 16,
  h1: { fontSize: 40, fontWeight: weight.bold, marginBottom: 12, lineHeight: 1.15 },
  h2: { fontSize: 32, fontWeight: weight.bold, marginBottom: 10, lineHeight: 1.2 },
  h3: { fontSize: 26, fontWeight: weight.semibold, marginBottom: 10, lineHeight: 1.25 },
  h4: { fontSize: 22, fontWeight: weight.semibold, marginBottom: 8, lineHeight: 1.3 },
  h5: { fontSize: 18, fontWeight: weight.semibold, marginBottom: 8, lineHeight: 1.35 },
  h6: { fontSize: 16, fontWeight: weight.semibold, marginBottom: 6, lineHeight: 1.4 },
};

/** Shared component overrides. `mode` only changes the few places that need a surface colour. */
function components(mode: 'light' | 'dark') {
  const surface = mode === 'light' ? neutral.white : '#242424';
  const border = mode === 'light' ? neutral.line : 'rgba(255,255,255,0.12)';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        // NOTE the --bui-font-regular override that the rest of the UI needs lives in
        // packages/app/src/brand.css, not here: MUI injects these styles before the imported
        // @backstage/ui stylesheet, so a :root rule here loses the cascade.
        //
        // These custom properties are safe to define here — nothing in @backstage/ui declares
        // them, so there is no cascade to lose. brand.css consumes them to style the BUI header,
        // which is plain CSS classes rather than MUI components.
        // The --tr-* brand variables and the header shape motif live in brand.css, not here:
        // MUI's CssBaseline `:root` styleOverrides did not emit them (verified in the browser).
        body: {
          fontFamily,
          // Flat surfaces only — the brand's depth comes from shapes, not washes.
          backgroundImage: 'none',
        },
        // Undefined links render browser-default blue; define both states explicitly.
        a: { color: mode === 'light' ? blueDeep : brand.blue, textDecoration: 'none' },
        'a:hover': {
          color: mode === 'light' ? blueDeeper : '#7FD4EA',
          textDecoration: 'underline',
        },
      },
    },
    // Pills, per the shape vocabulary. Gentle motion, no bounce.
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          textTransform: 'none' as const,
          fontWeight: weight.semibold,
          letterSpacing: 0,
          transition: `background-color ${motion.fast} ${motion.easeSoft}, box-shadow ${motion.fast} ${motion.easeSoft}`,
        },
        contained: { boxShadow: shadow.xs, '&:hover': { boxShadow: shadow.sm } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          // A transparent Clarity Blue fill — the working form of a brand colour.
          backgroundColor: alpha('blue', 20),
          color: mode === 'light' ? neutral.ink : neutral.white,
          fontWeight: weight.medium,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // The larger soft radius; cards are soft objects in this system, not boxes.
          borderRadius: radius.lg,
          backgroundColor: surface,
          // A soft low shadow instead of a hairline border — calmer, and it stops the page
          // reading as a grid of rectangles.
          border: mode === 'light' ? 'none' : `1px solid ${border}`,
          boxShadow: mode === 'light' ? shadow.md : 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiCardContent: { styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } } },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: radius.md },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        // Selected/hovered rows tint rather than fill.
        root: { '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: alpha('blue', 20) } },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: blueDeep, height: 3 } } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: weight.medium, letterSpacing: 0 },
      },
    },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: radius.sm } } },
    MuiTableCell: {
      styleOverrides: {
        // Backstage's catalog table sets `word-break: break-word`, which shatters entity names
        // mid-word in narrow columns ("CaitArt / icleIm / port"). Gabarito is wider than the stock
        // face, so it bites harder here. `normal` keeps each name a single unbreakable word, which
        // makes the auto-layout table give the column the width it actually needs.
        root: { wordBreak: 'normal' as const },
      },
    },
    // NOTE: no Backstage* component overrides here (BackstageHeader, BackstageSidebar,
    // BackstagePage, BackstageItemCardHeader, ...). This app runs the new frontend system, whose
    // chrome is @backstage/ui — verified in the browser, not one element in the DOM carries a
    // `Backstage*` class, so those overrides were silently doing nothing. The header, tabs, tags
    // and the plugin icon are styled in packages/app/src/brand.css against the real `bui-*`
    // classes. What still reaches the new sidebar is the `palette.navigation` block below.
  };
}

export const trLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      background: { default: neutral.white, paper: neutral.white },
      // Ink carries interaction; brand hues never become solid controls.
      primary: { main: neutral.ink, light: '#4A4A4A', dark: '#1A1A1A', contrastText: neutral.white },
      secondary: { main: blueDeep, light: brand.blue, dark: blueDeeper, contrastText: neutral.white },
      text: { primary: neutral.ink, secondary: neutral.mute },
      divider: neutral.line,
      error: { main: status.error },
      success: { main: status.success },
      warning: { main: '#B08900' },
      info: { main: blueDeep },
      banner: {
        info: blueDeep,
        error: status.error,
        text: neutral.ink,
        link: blueDeep,
        closeButtonColor: neutral.mute,
        warning: '#B08900',
      },
      border: neutral.line,
      textContrast: neutral.ink,
      textVerySubtle: neutral.muteSoft,
      textSubtle: neutral.mute,
      highlight: alpha('yellow', 40),
      errorBackground: alpha('pink', 20),
      warningBackground: alpha('yellow', 30),
      infoBackground: alpha('blue', 20),
      errorText: '#A33F3F',
      infoText: blueDeep,
      warningText: '#7A5E00',
      linkHover: blueDeeper,
      link: blueDeep,
      gold: brand.yellow,
      navigation: {
        background: neutral.ink,
        indicator: brand.blue,
        color: 'rgba(255,255,255,0.82)',
        selectedColor: neutral.white,
        navItem: { hoverBackground: 'rgba(255,255,255,0.08)' },
        submenu: { background: '#242424' },
      },
      // Status marks: the quiet functional pair, plus brand tints for the rest.
      status: {
        ok: status.success,
        warning: '#B08900',
        error: status.error,
        running: blueDeep,
        pending: neutral.mute,
        aborted: neutral.muteSoft,
      },
    },
  }),
  fontFamily,
  typography,
  defaultPageTheme: 'home',
  pageTheme: lightPages,
  components: components('light') as any,
});

export const trDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      background: { default: '#1B1B1B', paper: '#242424' },
      primary: { main: brand.blue, contrastText: neutral.ink },
      secondary: { main: brand.pink, contrastText: neutral.ink },
      text: { primary: '#F2F2F2', secondary: 'rgba(255,255,255,0.7)' },
      divider: 'rgba(255,255,255,0.12)',
      error: { main: status.error },
      success: { main: status.success },
      info: { main: brand.blue },
      border: 'rgba(255,255,255,0.12)',
      textContrast: neutral.white,
      textVerySubtle: 'rgba(255,255,255,0.4)',
      textSubtle: 'rgba(255,255,255,0.7)',
      highlight: alpha('yellow', 30),
      errorBackground: alpha('pink', 20),
      warningBackground: alpha('yellow', 20),
      infoBackground: alpha('blue', 20),
      link: brand.blue,
      linkHover: '#7FD4EA',
      gold: brand.yellow,
      navigation: {
        background: '#161616',
        indicator: brand.blue,
        color: 'rgba(255,255,255,0.82)',
        selectedColor: neutral.white,
        navItem: { hoverBackground: 'rgba(255,255,255,0.08)' },
        submenu: { background: '#242424' },
      },
      status: {
        ok: status.success,
        warning: brand.yellow,
        error: status.error,
        running: brand.blue,
        pending: 'rgba(255,255,255,0.6)',
        aborted: 'rgba(255,255,255,0.4)',
      },
    },
  }),
  fontFamily,
  typography,
  defaultPageTheme: 'home',
  pageTheme: darkPages,
  components: components('dark') as any,
});
